import React, { Dispatch, useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Action } from 'redux';

import lang from '../../lib/language';
import {
  getDividendPayers as getDividendPayersCompanies,
  getHighGrowthCompanies as getGrowthCompanies,
} from '../../lib/store/fundamental/index';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { RootState } from '../../lib/store/index';
import { isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import { Button, DividendPayers, HighGrowthCompanies, NoDataAvailable } from '../../components';
import { ButtonTypesEnum } from '../../components/Button/Button.types';
import NavButtons from '../../components/NavigationButtons/NavButtons';
import { useIsMobile } from '../../hooks';
import { NavigationState } from '../../util/constants';
import { UI_ROUTES } from '../../util/routes';

import styles from './LongTermInvestments.module.scss';

const LongTermInvestments = (props: ReduxProps) => {
  const {
    highGrowthCompanies,
    dividendPayers,
    isDividendPayersStatusReady,
    isHighGrowthCompaniesStatusReady,
    getDividendPayers,
    getHighGrowthCompanies,
  } = props;
  const isMobile = useIsMobile();
  const [currentTab, setCurrentTab] = useState<number>(0);

  const history = useHistory();

  const dividendsViewAllLink = {
    text: lang.commonViewAll(),
    link: UI_ROUTES.discover + UI_ROUTES.dividendPayersAndHighGrowthCompaniesPath,
    state: (dividendPayers.content.length && highGrowthCompanies.content.length)
      ? { currentTab: null, currentTabIndex: currentTab }
      : { currentTab },
  };

  useEffect(() => {
    if (!highGrowthCompanies.content.length || !dividendPayers.content.length) {
      getDividendPayers();
      getHighGrowthCompanies();
    }
  }, [dividendPayers.content.length, getDividendPayers, getHighGrowthCompanies, highGrowthCompanies.content.length]);

  const dividendsAndHighGrowthTabs = [
    {
      text: lang.commonDiscoverDividendPayers(),
      component: <DividendPayers dividendPayerContent={dividendPayers.content.slice(0, 10)} />,
    },
    {
      text: lang.commonDiscoverHighGrowthCompanies(),
      component: <HighGrowthCompanies companiesContent={highGrowthCompanies.content.slice(0, 10)} />,
    },
  ];

  const handlebuttonViewAll = () => {
    if (!currentTab) {
      return dividendPayers.content.length > 0 ? dividendsViewAllLink : undefined;
    }
    if (currentTab) {
      return highGrowthCompanies.content.length > 0 ? dividendsViewAllLink : undefined;
    }
    return undefined;
  };

  const handleTabChange = (index: number) => {
    setCurrentTab(index);
  };

  return (
    <>
      {isMobile ? (
        <>
          <div className={styles.titleWrapper}>
            <h6 className={styles.title}>
              {lang.commonDiscoverSubtitleLongTermInvestments()}
            </h6>
            <div>
              <Button
                variant={ButtonTypesEnum.Link}
                onClick={() => {
                  if (dividendsViewAllLink.state) {
                    history.push({
                      pathname: dividendsViewAllLink.link,
                      state: {
                        [NavigationState]: dividendsViewAllLink.state,
                      },
                    });
                  } else {
                    history.push(dividendsViewAllLink.link);
                  }
                }}
                className={styles.viewAll}
              >
                {dividendsViewAllLink.text}
              </Button>
            </div>
          </div>
          <div className={styles.longTermInvestmentsWrapper}>
            {
                          (isDividendPayersStatusReady && isHighGrowthCompaniesStatusReady)
                          && (dividendPayers.content.length > 0 || highGrowthCompanies.content.length > 0)
                          && (
                          <NavButtons
                            tabs={dividendsAndHighGrowthTabs}
                            sendCurrentTabIndex={handleTabChange}
                          />
                          )
                      }
          </div>
        </>
      ) : (
        <>
          <h6 className={styles.title}>{lang.commonDiscoverSubtitleLongTermInvestments()}</h6>
          <NavButtons
            tabs={dividendsAndHighGrowthTabs}
            rightSideLink={handlebuttonViewAll()}
            sendCurrentTabIndex={handleTabChange}
          />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  highGrowthCompanies: state.fundamental.highGrowthCompanies,
  dividendPayers: state.fundamental.dividendPayers,
  isDividendPayersStatusReady:
        isCallStatusReady(FundamentalCall.getDividendPayers, state.fundamental.statusByCall),
  isHighGrowthCompaniesStatusReady:
        isCallStatusReady(FundamentalCall.getHighGrowthCompanies, state.fundamental.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getHighGrowthCompanies: () => dispatch(getGrowthCompanies()),
  getDividendPayers: () => dispatch(getDividendPayersCompanies()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(LongTermInvestments);
